import {fromNullable} from "fp-ts/lib/Option"
import {history, WithModalStore} from "../App"
import {
  Booker,
  BookerFragFragment,
  BookingGutsFragment,
  BookingStatus,
  BookingStatusFragment,
  DefaultLocale,
  EnsureJourney,
  LocaleFragment,
  LoyaltyProgramDiscount,
  StripeCardFragment,
  SubmitBooking,
  SubmitBookingMutationVariables,
} from "../graphql/queries"
import {GraphQL} from "../graphql"
import {errorUnionToEither, handleError} from "../graphql/errors"
import {Divider, Form, Header, Label, Loader, Message, Popup} from "semantic-ui-react"
import {ModalStore} from "../actions"
import {defaultBookingDate} from "../reducers"
import {
  bookingDateTime,
  BookingDetails,
  BookingDetailsState,
  bookingDetailsStateToBookingDetails,
  BookingNotesWidget,
  detailsEditing,
  detailsValid,
  MapState,
} from "./BookingDetails"
import {BookerPicker} from "./BookerPicker"
import {PaymentInviteField, PaymentInviteOuterState} from "./PaymentInviteField"
import * as React from "react"
import {BookingWizardWrapper} from "./BookingWizardWrapper"
import {StripeCardInput} from "./StripeCard"
import {refetchQueries} from "./BookingWizard"
import {journeyToInputJourney} from "../model/journey"
import {bookingCardHasExpired, bookingToBookingStatusVariables} from "../model/booking"
import {hasCompleteJourney} from "./PlaceFields"
import {Auth0LockLink} from "./Auth0LockLink"
import {Charges} from "./Charges"
import {Place} from "../model/places/PlaceTypes"
import {emptyAirportState} from "./AirportStop"
import {BookingWizardDistance} from "./BookingWizardDistance"
import {PromotionsState, promotionStateToInput, PromotionsWidget} from "./Promotions"
import {emptyPassenger} from "../model/passenger/passenger"
import {isEmptyJourney} from "../state"
import {resolveLocale, UrlLocaleIdProp} from "../urlLocale"

export interface AddBookingState {
  forBooker: BookerFragFragment | null
  bookerFormOpen: boolean
  details: BookingDetailsState
  stripeCard: StripeCardFieldState
  paymentLink: PaymentInviteOuterState
  promotion: PromotionsState
  notes: string
  paymentSectionOpen: boolean
}

interface Props extends WithModalStore, UrlLocaleIdProp {
  isRepeatOrReturn: boolean
  initialState: AddBookingState | null
  onSave: () => void
  onState: (state: AddBookingState) => void
}

export interface BookerProp {
  booker: BookerFragFragment | null
}

export interface DefaultLocaleProp {
  defaultLocale: LocaleFragment
}

export interface BookingStatusProp {
  // null if no origin has been specified yet
  bookingStatus: BookingStatusFragment | null
  bookingStatusLoading: boolean
}

interface StateProps {
  state: AddBookingState
  mapState: MapState<AddBookingState>
  optimizeWaypoints: () => Promise<Array<Place> | null>
}

function AddBookingWizard(
  props: Props & BookerProp & DefaultLocaleProp & StateProps & BookingStatusProp,
) {
  const {state} = props
  const {mapState} = props

  function optimizeWaypoints() {
    props.modalStore.handleActionThrow(async () => {
      const waypoints = await props.optimizeWaypoints()
      if (waypoints) {
        mapState((state) => ({
          ...state,
          details: {
            ...state.details,
            journey: {...state.details.journey, waypoints},
          },
        }))
      }
    })
  }

  function render() {
    props.onState(state)

    return renderForm()
  }

  const {booker, modalStore} = props
  const isAdmin = booker ? booker.isAdmin : false

  const anythingLoading = props.bookingStatusLoading || state.stripeCard.loading
  const anyEditing =
    state.paymentLink.editing || detailsEditing(state.details) || state.bookerFormOpen
  const allValid =
    detailsValid(state.details, props.bookingStatus, isAdmin) &&
    !bookingCardHasExpired(state.stripeCard.card, state.details.bookingDate)
  const {forBooker, details, stripeCard} = state
  const payingBooker = isAdmin ? forBooker : booker
  const disabled = anyEditing
  const hasACompleteJourney = hasCompleteJourney(details)
  const detailsComplete = hasACompleteJourney && details.passengers.length > 0
  const enableSave =
    !anythingLoading && !anyEditing && allValid && (forBooker || booker) && state.paymentSectionOpen

  function renderForm() {
    return (
      <Form>
        {isAdmin && (
          <BookerField
            label="Booker"
            modalStore={props.modalStore}
            booker={forBooker}
            disabled={disabled}
            isAdmin={true}
            onBooker={(booker) => {
              mapState((state) => ({...state, forBooker: booker}))
            }}
            bookerFormOpen={state.bookerFormOpen}
            onBookerFormOpen={(open) => {
              mapState((state) => ({...state, bookerFormOpen: open}))
            }}
          />
        )}
        <BookingDetails
          bookingStatus={props.bookingStatus}
          bookingStatusLoading={props.bookingStatusLoading}
          state={state.details}
          mapState={(fn) => {
            mapState((state) => ({
              ...state,
              details: fn(state.details),
            }))
          }}
          disabled={disabled}
          modalStore={modalStore}
          onOptimizeWaypoints={optimizeWaypoints}
          isAdmin={isAdmin}
          forBooker={forBooker}
        />
        {/* Ensure the journey as soon as we can, while they enter passengers...*/}
        {hasACompleteJourney && (
          <GraphQL.Query
            query={EnsureJourney}
            variables={{journey: journeyToInputJourney(state.details.journey)}}
            render={({data: journeyData}) => {
              return journeyData && details.passengers.length > 0 ? (
                <>
                  <BookingNotesWidget
                    notes={state.notes}
                    onNotes={mapState && ((notes) => mapState((state) => ({...state, notes})))}
                    disabled={disabled}
                  />
                  <Divider />
                  {isAdmin && (
                    <BookingWizardDistance journey={state.details.journey} disabled={disabled} />
                  )}
                  {state.paymentSectionOpen ? (
                    <>
                      <GraphQL.Query
                        query={LoyaltyProgramDiscount}
                        variables={{
                          bookingDateTime: bookingDateTime(details),
                          journeyId: journeyData.ensureJourney.id,
                          forBookerId: forBooker ? forBooker.id : null,
                          bookingId: null,
                          promoCode: state.promotion.promoCode || "",
                          giftLoyaltyCredits: state.promotion.giftLoyaltyCredits,
                        }}
                        render={({data}) => {
                          if (data) {
                            const result = errorUnionToEither(data.loyaltyProgramDiscount)
                            const initial = result.fold(() => false, (data) => data.initial)
                            const referrer = result.fold(() => null, (data) => data.referrer)
                            const warning = result.fold((e) => e.message, () => "")
                            return (
                              <>
                                <Charges
                                  forBooker={forBooker}
                                  journeyId={journeyData.ensureJourney.id}
                                  booking={bookingDetailsStateToBookingDetails(state.details)}
                                  initialLoyaltyProgramDiscount={initial}
                                  giftLoyaltyProgramDiscount={state.promotion.giftLoyaltyCredits}
                                  stripeCard={stripeCard.card}
                                  state={{
                                    price: null,
                                    editingPrice: false,
                                  }}
                                  mapState={null}
                                  isAdmin={isAdmin}
                                  extraCharges={referrer ? [referrer] : []}
                                  onApprovePrice={null}
                                  modalStore={modalStore}
                                />
                                {warning && (
                                  <Message style={{display: "block"}} warning>
                                    Warning: {warning}
                                  </Message>
                                )}
                              </>
                            )
                          }
                          return <Loader inline active />
                        }}
                      />
                      {payingBooker && (
                        <>
                          <Divider />
                          <StripeCardField
                            modalStore={modalStore}
                            state={stripeCard}
                            payingBooker={payingBooker}
                            additionalStripeCard={null}
                            mapState={(fn) => {
                              mapState((state) => ({
                                ...state,
                                stripeCard: fn(state.stripeCard),
                              }))
                            }}
                            disabled={disabled}
                            bookingDate={details.bookingDate}
                          />
                        </>
                      )}
                      <PaymentInviteField
                        state={state.paymentLink}
                        mapState={
                          mapState &&
                          ((fn) => {
                            mapState((state) => {
                              return {
                                ...state,
                                paymentLink: fn(state.paymentLink),
                              }
                            })
                          })
                        }
                        disabled={disabled}
                      />
                      <PromotionsWidget
                        state={state.promotion}
                        mapState={(fn) => {
                          mapState((state) => {
                            return {
                              ...state,
                              promotion: fn(state.promotion),
                            }
                          })
                        }}
                        disabled={disabled}
                      />
                    </>
                  ) : (
                    <Form.Button
                      disabled={disabled}
                      content="Pricing and Payment"
                      icon="chevron right"
                      labelPosition="right"
                      positive
                      onClick={(e) => {
                        e.preventDefault()
                        mapState((state) => ({
                          ...state,
                          paymentSectionOpen: true,
                        }))
                      }}
                    />
                  )}
                </>
              ) : null
            }}
          />
        )}
        {!forBooker &&
          !booker &&
          detailsComplete && (
            <Message warning visible={true}>
              <Message.Header>Please log in or sign up</Message.Header>
              <p>
                To complete your booking please&nbsp;
                {
                  <Auth0LockLink initialScreen="login" email={null}>
                    Log in
                  </Auth0LockLink>
                }
                &nbsp;if you have an existing account or&nbsp;
                {
                  <Auth0LockLink initialScreen="signUp" email={state.details.passengers[0].email}>
                    Sign up
                  </Auth0LockLink>
                }
                &nbsp;for a new account. Sign up requires <b>just</b> your name, email and password.
              </p>
              <p>
                And, after you log in or sign up, if you register a credit card against your booking
                you will receive a 5% discount on the total price.
              </p>
            </Message>
          )}
        <Divider />
        <Form.Group>
          <Form.Button
            positive
            loading={props.bookingStatusLoading || state.stripeCard.loading}
            disabled={!enableSave}
            onClick={(e) => {
              e.preventDefault()
              if (enableSave && !disabled) {
                modalStore.handlePromiseThrow(handleSave())
              }
            }}
            content="Book Now"
          />
          <Form.Button
            disabled={disabled}
            content="Reset"
            onClick={(e) => {
              e.preventDefault()
              if (!disabled) {
                mapState(() => emptyState(props))
              }
            }}
          />
        </Form.Group>
      </Form>
    )
  }

  async function handleSave() {
    const {modalStore} = props
    const {
      forBooker,
      details,
      stripeCard: {card: stripeCard},
      paymentLink: {toEmail},
      promotion,
    } = state
    const variables: SubmitBookingMutationVariables = {
      forBookerId: forBooker ? forBooker.id : null,
      notes: state.notes,
      details: bookingDetailsStateToBookingDetails(details),
      stripeCardId: stripeCard ? stripeCard.id : null,
      paymentLinkToEmail: toEmail ? {string: toEmail} : null,
      promotion: promotionStateToInput(promotion),
    }

    const isEmailVerified = !forBooker || forBooker.isEmailVerified

    const message = (booking: BookingGutsFragment) =>
      `Your new booking number ${booking.number} has been submitted` +
      (isEmailVerified
        ? " and you should shortly receive a confirmation email."
        : ".  Please verify your new account, using the email we sent, so we can send your" +
          " booking confirmation.")

    // Migrated from BookingSubmittedModal
    const data = await modalStore.applyModal({
      pending: ["Submitting booking", "Submitting booking..."],
      ok: (booking) => ["Booking submitted", message(booking)],
      error: (m) => ["Booking failure", m],
      action: async () => {
        const data = await GraphQL.mutate(SubmitBooking, variables, {
          refetchQueries: refetchQueries(details.locale.id, {
            forBookerId: forBooker ? forBooker.id : null,
          }),
        })
        return errorUnionToEither(data.submitBooking)
      },
    })
    data.map((x) => {
      props.onSave()
      history.push("/booking/" + x.number)
    })
  }

  return render()
}

export function emptyBookingDetails(props: BookerProp & DefaultLocaleProp & UrlLocaleIdProp): BookingDetailsState {
  const {booker, defaultLocale, urlLocaleId} = props
  return {
    journey: {
      origin: null,
      destination: null,
      waypoints: [],
    },
    airportPickup: emptyAirportState(),
    airportDropoff: emptyAirportState(),
    bookingDate: defaultBookingDate(),
    bookingHour: 10,
    bookingMinute: 0,
    passengerCount: "1",
    babySeatsForward: 0,
    babySeatsBackward: 0,
    boosterSeats: 0,
    wantPeopleMover: false,
    passengers: [],
    locale: resolveLocale(urlLocaleId, booker, defaultLocale),
    initialPassenger: emptyPassenger(),
    editingPassenger: false,
    editingPassengerIndex: null,
    editingDate: false,
  }
}

function emptyState(props: BookerProp & DefaultLocaleProp & UrlLocaleIdProp): AddBookingState {
  const {booker} = props
  return {
    forBooker: booker && booker.isAdmin ? null : booker,
    bookerFormOpen: false,
    details: emptyBookingDetails(props),
    notes: "",
    stripeCard: {
      loading: false,
      card: null,
    },
    paymentLink: {
      toEmail: null,
      editing: false,
    },
    promotion: {
      promoCode: null,
      giftLoyaltyCredits: booker ? booker.giftLoyaltyCredits : false,
    },
    paymentSectionOpen: false,
  }
}

function WithBookingStatus(props: Props & BookerProp & DefaultLocaleProp & StateProps) {
  return (
    <GraphQL.Query
      query={BookingStatus}
      variables={bookingToBookingStatusVariables(props.state.details)}
      render={({data, loading}) => {
        if (!data) {
          return <Loader active />
        }
        return (
          <AddBookingWizard
            {...props}
            bookingStatus={data && data.bookingStatus}
            bookingStatusLoading={loading}
          />
        )
      }}
    />
  )
}

class AddBookingWizardState extends React.Component<
  Props & BookerProp & DefaultLocaleProp,
  {state: AddBookingState}
> {
  constructor(props: Props & BookerProp & DefaultLocaleProp) {
    super(props)
    const {initialState, isRepeatOrReturn, booker, defaultLocale, urlLocaleId} = props
    this.state = {
      state: initialState
        ? {
          ...initialState,

          // If it's not a repeat or return booking and forBooker is null it's because the state
          // was saved from before the user logged in (which is actually the entire point of
          // initialState). In that case replace it with the logged in booker if present.
          forBooker: isRepeatOrReturn || initialState.forBooker ? initialState.forBooker : booker,

          details: {
            ...initialState.details,

            // If the journey is empty, then don't reuse the locale from the saved state. Instead
            // use the same locale as we would use if there was no saved state at all (i.e. result
            // from resolveLocale()).
            locale: isEmptyJourney(initialState.details.journey)
              ? resolveLocale(urlLocaleId, booker, defaultLocale)
              : initialState.details.locale,
          }
        }
        : emptyState(props)
    }
  }

  render() {
    return <>
      <Header as="h1">New Booking</Header>
      <BookingWizardWrapper
        defaultState={{
          mapJourney: this.state.state.details.journey,
          mapLocale: this.state.state.details.locale,
        }}
        render={(props) => {
          return (
            <WithBookingStatus
              {...this.props}
              state={this.state.state}
              mapState={(fn) => {
                this.setState(({state}) => {
                  state = fn(state)
                  props.setMapState({
                    mapJourney: state.details.journey,
                    mapLocale: state.details.locale,
                  })
                  return {state}
                })
              }}
              optimizeWaypoints={props.optimizeMapJourney}
            />
          )
        }}
      />
    </>
  }
}

function WithBooker(props: Props & DefaultLocaleProp) {
  return (
    <GraphQL.Query
      query={Booker}
      variables={{}}
      render={({data}) => {
        if (data !== null) {
          return handleError(errorUnionToEither(data.booker), (data) => (
            <AddBookingWizardState {...props} booker={data.booker} />
          ))
        }
        // This should not happen, as Booker is loaded in InitializeSession
        return <Loader active />
      }}
    />
  )
}

function WithDefaultLocale(props: Props) {
  return (
    <GraphQL.Query
      query={DefaultLocale}
      variables={{
        id: props.urlLocaleId,
      }}
      render={({data}) => {
        if (data) {
          return <WithBooker {...props} defaultLocale={data.defaultLocale} />
        }
        return <Loader active />
      }}
    />
  )
}

export {WithDefaultLocale as AddBookingWizard}

export function ErrorLabel({error}: {error: string | null}) {
  return error ? <Label className="prompt" pointing content={error} /> : null
}

export function BookerField(props: {
  modalStore: ModalStore
  booker: BookerFragFragment | null
  label: string | null
  disabled: boolean
  onBooker: ((booker: BookerFragFragment | null) => void) | null
  isAdmin: boolean
  bookerFormOpen: boolean
  onBookerFormOpen: ((open: boolean) => void) | null
}) {
  const {booker, disabled, onBooker, isAdmin, label} = props
  return (
    <Form.Field>
      {label && <label>{label}</label>}
      {onBooker ? (
        <BookerPicker
          modalStore={props.modalStore}
          booker={booker}
          onBooker={onBooker}
          disabled={disabled}
          bookerFormOpen={props.bookerFormOpen}
          onBookerFormOpen={props.onBookerFormOpen}
        />
      ) : (
        <BookerLink isAdmin={isAdmin} booker={booker} />
      )}
    </Form.Field>
  )
}

export function BookerLink(props: {isAdmin: boolean; booker: BookerFragFragment | null}) {
  const {booker, isAdmin} = props
  if (!booker) {
    return null
  } else if (isAdmin) {
    return (
      <a
        onClick={(e) => {
          e.preventDefault()
          history.push("/profile/" + booker.id)
        }}
        href="#"
      >
        {booker.name} ({booker.email})
      </a>
    )
  } else {
    return (
      <>
        {booker.name} ({booker.email})
      </>
    )
  }
}

export interface StripeCardFieldState {
  card: StripeCardFragment | null
  loading: boolean
}

export function StripeCardField(props: {
  state: StripeCardFieldState
  mapState: MapState<StripeCardFieldState> | null
  payingBooker: BookerFragFragment
  modalStore: ModalStore
  additionalStripeCard: StripeCardFragment | null
  disabled: boolean
  bookingDate: string
}) {
  const {mapState} = props
  return (
    <Form.Field error={bookingCardHasExpired(props.state.card, props.bookingDate)}>
      <Popup
        trigger={<label>Registered Credit Card</label>}
        content={`Register a credit card and receive a 5% booking discount.  Your card will not be
      charged until the booking is complete, or in the case of a late cancellation.`}
        wide
      />
      <StripeCardInput
        payingBooker={props.payingBooker}
        modalStore={props.modalStore}
        stripeCard={fromNullable(props.state.card)}
        additionalStripeCards={fromNullable(props.additionalStripeCard).fold([], (x) => [x])}
        onStripeCard={
          mapState &&
          (async (loadingCard) => {
            // TODO refactor this so the "loading" state is passed in instead of it being
            // maintained in two places.
            let card
            mapState((state) => ({...state, loading: true}))
            try {
              card = await loadingCard
            } finally {
              mapState((state) => ({...state, loading: false}))
            }
            const card2 = card
            return () =>
              card2.map((card) => mapState((state) => ({...state, card: card.toNullable()})))
          })
        }
        disabled={props.disabled}
        bookingDate={props.bookingDate}
      />
    </Form.Field>
  )
}



// WEBPACK FOOTER //
// ./src/components/AddBookingWizard.tsx